import { type GeoData, getCookie } from "@identity/utils";

export const ONETRUST_CONSENT_COOKIE_NAME = "OptanonConsent";
export const ONETRUST_MODAL_CLOSED_COOKIE_NAME = "OptanonAlertBoxClosed";
export const ONETRUST_TRACKING_GROUP = "C0004";

const oneTrustCategoriesMap = {
  C0001: "necessary",
  C0002: "performance",
  C0003: "functional",
  C0004: "targeting",
  C0005: "social",
};

export const getDefaultOneTrustCategories = (
  defaultValue: boolean = false,
  overwrite: Partial<Categories> = {},
): Required<Categories> => {
  return {
    necessary: defaultValue,
    performance: defaultValue,
    functional: defaultValue,
    targeting: defaultValue,
    social: defaultValue,
    ...overwrite,
  };
};

const checkOneTrustGroups = (groupName: string) => {
  const optanonCookie = getCookie(ONETRUST_CONSENT_COOKIE_NAME);
  if (!optanonCookie) {
    return undefined;
  }
  const groups = getOptanonConsent(optanonCookie)?.groups;
  return groups ? groups.includes(groupName) : undefined;
};

export const checkOneTrustOptOut = (): boolean | undefined => {
  return checkOneTrustGroups(`${ONETRUST_TRACKING_GROUP}:0`);
};

export const checkOneTrustOptIn = (): boolean | undefined => {
  return checkOneTrustGroups(`${ONETRUST_TRACKING_GROUP}:1`);
};

export const checkOneTrustModalClosed = () => {
  return getCookie(ONETRUST_MODAL_CLOSED_COOKIE_NAME) !== null;
};

export const setOneTrustGeolocation = (geo: GeoData) => {
  if (geo.country && geo.continent && geo.region) {
    window.OneTrust = {
      geolocationResponse: {
        stateCode: geo.region,
        countryCode: geo.country,
        regionCode: geo.continent,
      },
    } as typeof window.OneTrust;
  }
};

export const hasOptanonGroups = (
  optanonCookie?: OptanonConsent,
): optanonCookie is OptanonConsent => {
  return optanonCookie?.groups !== undefined && optanonCookie.groups.length > 0;
};

export const getOneTrustConsentCategories = (optanonCookie: OptanonConsent) => {
  const groups = optanonCookie?.groups ?? "";
  const categories: Required<Categories> = getDefaultOneTrustCategories();
  return Object.entries(oneTrustCategoriesMap).reduce((acc, [key, value]) => {
    if (groups.includes(`${key}:1`)) {
      acc[value] = true;
    }
    return acc;
  }, categories);
};

export type OptanonConsent = {
  datestamp: string;
  groups: string;
  geolocation: string;
  version: string;
};
export const getOptanonConsent = (cookie: string) => {
  const params = new URLSearchParams(cookie);
  const result: Record<string, string> = {};

  params.forEach((value, key) => {
    result[key] = value;
  });

  return result as OptanonConsent;
};
